// Anita Week 3 / Day 2 — Motion: Faster, Slower, Farther.
// Content is supplied verbatim by the curriculum author; this file only places
// that content into the existing Nimny Lab lesson schema. No pedagogy is invented.
// SAFETY: soft push only, safe object, safe surface. No throwing, no stairs, no roads, no glass.

const day2 = {
  id: 'day-2',
  studentId: 'anita',
  weekId: 'week-3',
  dayId: 'day-2',
  status: 'active',
  title: 'Движение: быстрее, медленнее, дальше / Motion: Faster, Slower, Farther',
  shortTitle: 'Движение / Motion',
  theme:
    'Анита учится наблюдать движение, сравнивать расстояние и понимать, что результат зависит от силы толчка и поверхности. / Anita learns to observe motion, compare distance, and understand that results can depend on push strength and surface.',
  estimatedTimeLabel: '60–90 минут / 60–90 minutes',
  lessonTiming: { minMinutes: 60, maxMinutes: 90, breaks: 2 },
  bilingualMode: 'ru-primary-en-helper',
  courseLayers: ['reading', 'math', 'science', 'motion', 'data', 'spelling', 'creative', 'speech'],
  readingLevel: 'grade-2',
  nimnyIntro:
    'Нимни ставит маленькую машинку на старт. Машинка может ехать близко или далеко, быстро или медленно. Сегодня Анита узнает: движение можно наблюдать, измерять и сравнивать. / Nimny puts a small toy car at the start. The car can go near or far, fast or slow. Today Anita learns that motion can be observed, measured and compared.',
  goals: [
    'Понять слова движение, расстояние, старт, финиш. / Understand motion, distance, start, finish.',
    'Сравнивать дальше, ближе, быстрее, медленнее. / Compare farther, closer, faster, slower.',
    'Записывать результаты в таблицу. / Record results in a table.',
    'Решать задачи на расстояние и разницу. / Solve distance and difference problems.',
    'Сделать мини-опыт с безопасным предметом. / Do a small safe experiment.',
    'Объяснить результат родителю. / Explain the result to parent.'
  ],
  vocabulary: [
    { term: 'движение / motion', definition: 'когда предмет меняет место. / when an object changes place.' },
    { term: 'расстояние / distance', definition: 'как далеко предмет прошёл. / how far an object travelled.' },
    { term: 'старт / start', definition: 'место, где движение начинается. / where motion begins.' },
    { term: 'финиш / finish', definition: 'место, где движение заканчивается. / where motion ends.' },
    { term: 'быстрее / faster', definition: 'за меньшее время или с большей скоростью. / in less time or at greater speed.' },
    { term: 'медленнее / slower', definition: 'не так быстро. / not as fast.' },
    { term: 'поверхность / surface', definition: 'место, по которому движется предмет. / the surface the object moves on.' },
    { term: 'результат / result', definition: 'что получилось после опыта. / what came out of the experiment.' }
  ],
  onlineTasks: [
    {
      id: 'reading-words',
      type: 'text',
      title: 'Слова движения / Motion Words',
      prompt: 'Прочитай по-русски:\nдвижение\nрасстояние\nстарт\nфиниш\nбыстрее\nмедленнее\nповерхность\nрезультат',
      ruPrimaryText: 'Прочитай 8 русских слов движения.',
      enHelpText: 'Read the Russian motion words first. English is only for help.',
      hint: 'Слова «расстояние» и «поверхность» читай по частям. / Read longer words by syllables.',
      answer: 'Анита пробует читать русские слова. / Anita attempts the Russian reading.'
    },
    {
      id: 'reading-sentences',
      type: 'text',
      title: 'Маленькое чтение / Short Reading',
      prompt: 'Прочитай по-русски:\nМашинка начинает на старте.\nОна едет до финиша.\nЯ измеряю расстояние.',
      ruPrimaryText: 'Прочитай 3 короткие фразы по-русски.',
      enHelpText: 'The car starts at the start. It goes to the finish. I measure the distance.',
      hint: 'Сначала попробуй сама. / Try it first.',
      answer: 'Анита пробует читать 3 фразы. / Anita attempts the short reading.'
    },
    {
      id: 'math-car-distance',
      type: 'text',
      title: 'Расстояние машинки / Car Distance',
      prompt:
        'Нимни толкнула машинку 3 раза.\nПопытка 1 — 18 см.\nПопытка 2 — 25 см.\nПопытка 3 — 21 см.\n\nОтветь:\n1. Какая попытка была самой дальней?\n2. Какая попытка была самой короткой?\n3. На сколько см попытка 2 дальше попытки 1?\n4. На сколько см попытка 2 дальше попытки 3?',
      ruPrimaryText: 'Сравни 3 попытки движения машинки и реши 4 вопроса.',
      enHelpText: 'Compare 3 car attempts (18, 25, 21 cm). Answer 4 questions.',
      answer: '1) Попытка 2. 2) Попытка 1. 3) 25 − 18 = 7 см. 4) 25 − 21 = 4 см.'
    },
    {
      id: 'math-total-distance',
      type: 'text',
      title: 'Сумма расстояний / Total Distance',
      prompt:
        'Машинка проехала:\n20 см\n15 см\n30 см\n\nОтветь:\n1. Сколько всего сантиметров?\n2. Если добавить ещё 10 см, сколько будет?\n3. На сколько 30 см больше, чем 15 см?',
      ruPrimaryText: 'Найди сумму расстояний и разницу.',
      enHelpText: 'Distances: 20, 15, 30 cm. Find the total, add 10 cm, and the difference between 30 and 15.',
      answer: '1) 20 + 15 + 30 = 65 см. 2) 65 + 10 = 75 см. 3) 30 − 15 = 15 см.'
    },
    {
      id: 'science-motion-test',
      type: 'text',
      title: 'Мини-опыт движения / Mini Motion Test',
      prompt:
        'С родителем выбери безопасный предмет: игрушечная машинка, кубик или маленькая коробочка.\nСделай 2 попытки движения на безопасной поверхности.\nЗапиши:\nПопытка 1: ______.\nПопытка 2: ______.\nЧто дальше?\n\nЕсли нет линейки, сравни словами: ближе, дальше, примерно одинаково.',
      ruPrimaryText: 'С родителем сделай 2 безопасные попытки движения и сравни результат.',
      enHelpText: 'With a parent, pick a safe item. Make 2 soft attempts on a safe surface. Record and compare.',
      hint: 'Мягкий толчок. Не бросать предметы. Не делать опыт на лестнице или дороге. / Soft push only. Never throw objects.',
      answer: 'Open-ended. Parent checks that Anita observes and compares safely.'
    },
    {
      id: 'data-order-results',
      type: 'text',
      title: 'Расположи результаты / Order the Results',
      prompt: 'Результаты опыта:\n12 см\n28 см\n19 см\n25 см\n\nРасположи от меньшего к большему.',
      ruPrimaryText: 'Расположи 4 числа от меньшего к большему.',
      enHelpText: 'Order 4 results from smallest to largest: 12, 28, 19, 25.',
      answer: '12 см, 19 см, 25 см, 28 см.'
    },
    {
      id: 'spelling-motion',
      type: 'text',
      title: 'Правописание / Spelling',
      prompt: 'Напиши:\nстарт\nфиниш\nбыстро\nмедленно\n\nВставь буквы:\nст_рт\nф_ниш\nбыстр_\nмедленн_',
      ruPrimaryText: 'Напиши 4 слова и вставь пропущенные буквы.',
      enHelpText: 'Write 4 words: start, finish, fast, slow. Then fill the missing letters.',
      answer: 'старт, финиш, быстро, медленно.'
    },
    {
      id: 'creative-motion-lab',
      type: 'text',
      title: 'Лаборатория движения / Motion Lab Page',
      prompt:
        'Нарисуй старт, финиш и путь предмета.\nНапиши:\nПредмет: ______.\nОн двигался ______.\nМой вывод: ______ дальше.',
      ruPrimaryText: 'Нарисуй старт, финиш и путь. Напиши один вывод.',
      enHelpText: 'Draw start, finish and path. Write the object and one conclusion.',
      answer: 'Open-ended. Accept if Anita draws a start/finish path and writes one conclusion.'
    },
    {
      id: 'speech-motion-explain',
      type: 'text',
      title: 'Объясни родителю / Explain to Parent',
      prompt: 'Скажи родителю по-русски:\nЯ наблюдала движение.\nЯ сравнила расстояние.\nМой вывод: ______ дальше.',
      ruPrimaryText: 'Объясни родителю по-русски движение и вывод.',
      enHelpText: 'Tell a parent in Russian: I observed motion. I compared the distance. My conclusion: ___ went farther.',
      answer: 'Accept if Anita explains motion and distance in Russian with support.'
    }
  ],
  notebookInstructions: [
    'Если нет принтера:',
    '1. Напиши слова дня.',
    '2. Прочитай 3 фразы.',
    '3. Реши задачи про расстояние.',
    '4. Сделай 2 безопасные попытки движения.',
    '5. Запиши или скажи результат.',
    '6. Нарисуй старт, финиш и путь.',
    '7. Объясни родителю вывод.'
  ],
  printWorksheet: {
    title: 'Движение / Motion',
    subtitle: 'Неделя 3 · День 2 / Week 3 · Day 2',
    pages: [
      {
        title: 'Маршрут дня / Route',
        blocks: [
          { type: 'text', text: 'Миссия: Нимни исследует движение. / Nimny explores motion.' },
          { type: 'list', items: [
            'Слова дня (8 слов). / Words of the day (8 words).',
            'Маленькое чтение: 3 фразы. / Short reading: 3 sentences.',
            'English help мелким шрифтом — только если трудно. / English help in small print — only if hard.'
          ] }
        ]
      },
      {
        title: 'Математика движения / Motion Math',
        blocks: [
          { type: 'text', text: 'Задачи и порядок результатов. / Tasks and result order.' },
          { type: 'list', items: [
            '3 попытки: 18 см, 25 см, 21 см. / 3 car attempts.',
            'Сумма расстояний: 20 см, 15 см, 30 см. / Total distance.',
            'Расположить результаты: 12, 28, 19, 25. / Order results.'
          ] }
        ]
      },
      {
        title: 'Мини-опыт движения / Mini Motion Lab',
        blocks: [
          { type: 'text', text: 'Безопасный опыт и рисунок. / Safe experiment and drawing.' },
          { type: 'list', items: [
            '2 попытки с безопасным предметом и мягким толчком. / 2 attempts with a safe item and a soft push.',
            'Правописание: старт, финиш, быстро, медленно. / Spelling.',
            'Нарисуй старт, финиш и путь. / Draw start, finish and path.',
            'Напиши один вывод. / Write one conclusion.',
            'Parent Check.',
            'Notebook Mode fallback — если нет принтера, всё то же в тетради. / Notebook Mode fallback.'
          ] }
        ]
      }
    ]
  },
  parentGuide: {
    goal: 'Анита понимает, что движение можно наблюдать, измерять и сравнивать.',
    mathGoal: 'Сложение, вычитание, сравнение и порядок чисел до 100.',
    russianGoal: 'Прочитать слова движение, расстояние, старт, финиш, быстрее, медленнее, поверхность, результат.',
    readingWritingGoal: 'Прочитать 3 фразы, написать 4 слова, подписать рисунок движения.',
    practicalGoal: 'Сделать безопасный мини-опыт с движением и сравнить 2 результата.',
    parentTheoryCard: {
      title: 'Как объяснить движение ребёнку',
      purpose: 'Помочь родителю объяснить, что движение — это изменение места, а расстояние можно сравнивать.',
      keyMessage: 'Движение можно увидеть, измерить и записать. Если результат разный, учёный спрашивает: «Что изменилось?»',
      parentScript: [
        '«Движение — это когда предмет меняет место.»',
        '«Старт — где предмет начал двигаться.»',
        '«Финиш — где предмет остановился.»',
        '«Расстояние — как далеко он прошёл.»',
        '«Если две попытки разные, мы сравниваем результат.»'
      ],
      practiceSteps: [
        'Отметьте старт.',
        'Сделайте одну мягкую попытку.',
        'Сделайте вторую мягкую попытку.',
        'Сравните: ближе, дальше, примерно одинаково.',
        'Запишите или нарисуйте вывод.'
      ],
      doNotSay: [
        'Не требовать сильного толчка.',
        'Не разрешать бросать предметы.',
        'Не превращать опыт в соревнование на силу.'
      ],
      paperOnlyNotes: [
        'Опыт можно выполнить в тетради без принтера.',
        'Использовать только безопасные предметы и безопасную поверхность.',
        'Не использовать лестницу, дорогу, стекло или хрупкие предметы.'
      ]
    },
    whatThisLessonTeaches: 'Анита учится видеть движение как наблюдаемый и измеряемый процесс.',
    keyMessage: 'Я могу наблюдать движение, измерить расстояние и сделать вывод.',
    howToExplain: 'Скажите: «Если машинка едет дальше, это результат. Мы можем сравнить результаты и подумать, почему они разные.»',
    exampleDialogue:
      'Parent: Где старт?\nChild: Здесь.\nParent: Где финиш?\nChild: Где машинка остановилась.\nParent: Какая попытка дальше?\nChild: Вторая.\nParent: Какой вывод?\nChild: Вторая попытка дальше.',
    readingSupport: 'Слова «расстояние», «поверхность», «медленнее» можно читать по частям.',
    russianSpeechGoal: 'Анита говорит: «Я наблюдала движение. Мой вывод: ___ дальше.»',
    mathAnswerKey:
      'math-car-distance: 1) Попытка 2. 2) Попытка 1. 3) 7 см. 4) 4 см.\nmath-total-distance: 1) 65 см. 2) 75 см. 3) 15 см.\ndata-order-results: 12, 19, 25, 28.',
    spellingAnswerKey: 'старт\nфиниш\nбыстро\nмедленно',
    answerKey: [
      { taskId: 'reading-words', answer: 'Попытка чтения 8 русских слов.' },
      { taskId: 'reading-sentences', answer: 'Попытка чтения 3 коротких фраз.' },
      { taskId: 'math-car-distance', answer: 'попытка 2; попытка 1; 7 см; 4 см.' },
      { taskId: 'math-total-distance', answer: '65 см; 75 см; 15 см.' },
      { taskId: 'science-motion-test', answer: 'Безопасное сравнение 2 попыток движения.' },
      { taskId: 'data-order-results', answer: '12, 19, 25, 28.' },
      { taskId: 'spelling-motion', answer: 'старт, финиш, быстро, медленно.' },
      { taskId: 'creative-motion-lab', answer: 'Рисунок старта, финиша и один вывод.' },
      { taskId: 'speech-motion-explain', answer: 'Объяснение движения по-русски.' }
    ],
    check: [
      'Анита прочитала слова и фразы.',
      'Анита решила math tasks.',
      'Анита сравнила результаты движения.',
      'Анита сделала рисунок старта и финиша.',
      'Анита сказала вывод по-русски.'
    ],
    spellingCheck: 'Проверить старт, финиш, быстро, медленно.',
    readingCheck: 'Главное — попытка чтения и понимание слов.',
    practicalCheck: 'Опыт безопасный, мягкий, без бросания предметов.',
    drawingCriteria: 'Есть старт, финиш, путь и один вывод.',
    commonMistakes: [
      '«Нужно толкать сильно.» Правильно: «Нужно толкать безопасно и наблюдать.»',
      '«Если результат другой, это ошибка.» Правильно: «Разный результат — повод подумать, что изменилось.»',
      '«Дальше всегда лучше.» Правильно: «Дальше — просто другой результат. Учёный сравнивает.»'
    ],
    discussionPrompts: [
      'Что такое старт?',
      'Что такое финиш?',
      'Почему две попытки могут быть разными?',
      'Как можно записать результат?'
    ],
    ifEasy: 'Пусть Анита сделает 3 попытки и расположит их от меньшего расстояния к большему.',
    ifDifficult: 'Сравнить только словами: ближе, дальше, примерно одинаково.',
    rewardGuidance: 'Базовая награда — 3 нима. Бонус за аккуратный рисунок движения или ясный вывод.',
    tokenReward: 'Нимы — это призовые токены курса, а не оплата.',
    parentOnlyNotes: 'Не делать опыт на лестнице, дороге или рядом с хрупкими предметами. Не превращать опыт в силовую игру.',
    safetyNotes: 'Использовать мягкий толчок, безопасный предмет и безопасную поверхность. Не бросать предметы.'
  },
  reward: {
    unit: 'nim',
    unitName: 'ним',
    base: 3,
    max: 5,
    label: 'Награда за миссию',
    display: '3 нима',
    bonuses: [
      { id: 'neat-motion-drawing', label: 'Бонус за аккуратный рисунок старта и финиша', amount: 1 },
      { id: 'clear-conclusion', label: 'Бонус за ясный вывод по-русски', amount: 1 }
    ]
  },
  completionRequirements: {
    requiredTaskIds: [
      'reading-words',
      'reading-sentences',
      'math-car-distance',
      'math-total-distance',
      'science-motion-test',
      'data-order-results',
      'spelling-motion',
      'creative-motion-lab',
      'speech-motion-explain'
    ],
    allowNotebookCompletion: true
  },
  nextLessonId: 'anita-week-3-day-3'
};

export default day2;
